import { useEffect } from 'react';
import { useShadowRootElements } from '@backstage/plugin-techdocs-react';
import { useApi, fetchApiRef } from '@backstage/core-plugin-api';
import { isDrawioImgSource } from './hooks';
import { DrawioProps } from './props';

const VIEWER_BASE_URL = 'https://viewer.diagrams.net/?dark=0#R';
const PROCESSED_ATTR = 'data-drawio-processed';

/**
 * Creates a viewer.diagrams.net iframe and inserts it after the given element.
 */
const createViewerIframe = (
  el: HTMLElement,
  xml: string,
  width: string,
  height: string,
) => {
  const iframe = document.createElement('iframe');
  iframe.setAttribute('style', `width:${width};height:${height};border:none;`);
  iframe.setAttribute('frameborder', '0');
  iframe.setAttribute('sandbox', 'allow-scripts allow-same-origin');
  iframe.setAttribute('referrerpolicy', 'no-referrer');
  iframe.src = `${VIEWER_BASE_URL}${encodeURIComponent(xml)}`;
  el.parentNode?.insertBefore(iframe, el.nextSibling);
  el.style.display = 'none';
};

/**
 * Fetches a .drawio file from an <img> src and renders it via the viewer iframe.
 */
const makeDiagram = (el: HTMLImageElement, fetchFn: typeof fetch) => {
  const width = el.style.width || '100%';
  const height = el.style.height || '500px';

  el.style.display = 'none';

  fetchFn(el.src)
    .then((response) => {
      if (!response.ok) return;
      return response.text();
    })
    .then((xml) => {
      if (xml) {
        createViewerIframe(el, xml, width, height);
      }
    })
    .catch(() => {
      // Restore image visibility on fetch failure
      el.style.display = '';
    });
};

/**
 * Renders an embedded mxgraph div (with data-mxgraph JSON attribute) via the viewer iframe.
 */
const makeMxGraphDiagram = (el: HTMLElement) => {
  const dataAttr = el.getAttribute('data-mxgraph');
  if (!dataAttr) return;

  try {
    const config = JSON.parse(dataAttr);
    if (!config.xml) return;
    createViewerIframe(el, config.xml, el.style.maxWidth || '100%', '600px');
  } catch {
    // invalid data-mxgraph attribute
  }
};

export const DrawioAddon = (properties?: DrawioProps) => {
  const { fetch: backstageFetch } = useApi(fetchApiRef);
  const images = useShadowRootElements<HTMLImageElement>(['img']);
  const mxgraphDivs = useShadowRootElements<HTMLElement>(['.mxgraph']);

  useEffect(() => {
    images.forEach((image) => {
      if (image.hasAttribute(PROCESSED_ATTR)) return;
      if (!isDrawioImgSource(image.src)) return;

      image.setAttribute(PROCESSED_ATTR, 'true');
      makeDiagram(image, backstageFetch);
    });
  }, [images, properties, backstageFetch]);

  useEffect(() => {
    mxgraphDivs.forEach((div) => {
      if (div.hasAttribute(PROCESSED_ATTR)) return;

      div.setAttribute(PROCESSED_ATTR, 'true');
      makeMxGraphDiagram(div);
    });
  }, [mxgraphDivs, properties]);

  return null;
};
